import { Heart } from '@phosphor-icons/react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

import type { ActivityDetail, ReportReason } from '../api'
import { canLeaveActivity } from '../access'
import { ConfirmDialog } from './confirm-dialog'

const REPORT_REASONS: ReportReason[] = [
  'spam',
  'harassment',
  'inappropriate_content',
  'unsafe_behavior',
  'scam',
  'other',
]

interface ActionPanelProps {
  activity: ActivityDetail
  /** TASK-4 (maintainer): a join without a picture is rejected server-side. */
  profileIncomplete?: boolean
  saved: boolean
  saveBusy: boolean
  joinBusy: boolean
  leaveBusy: boolean
  canBlock: boolean
  canReport: boolean
  onToggleSave: () => void
  onReport: (reason: ReportReason, description: string) => Promise<void>
  onBlock: () => Promise<void>
  onJoin: () => Promise<boolean>
  onLeave: () => Promise<boolean>
}

type ParticipationKind =
  | 'creator'
  | 'joined'
  | 'pending'
  | 'waitlisted'
  | 'removed'
  | 'waitlist'
  | 'join-open'
  | 'join-approval'
  | 'join-invite'
  | 'join-profile'
  | 'closed'
  | 'full'
  | 'cancelled'
  | 'unavailable'

type ConfirmKind = 'join' | 'leave'

interface ParticipationCopy {
  titleKey: string
  bodyKey: string
  bodyValues?: Record<string, string | number>
}

interface ActionPanelProps {
  activity: ActivityDetail
  bodyValues?: Record<string, string | number>
}

function participationKind(
  activity: ActivityDetail,
  profileIncomplete = false,
): ParticipationKind {
  if (profileIncomplete && activity.canJoin && activity.availableSpots > 0) {
    // TASK-4: the backend rejects a join without a picture, so the client
    // surfaces the same rule before the request instead of a generic error.
    return 'join-profile'
  }

  if (activity.viewerStatus === 'creator') return 'creator'
  if (activity.viewerStatus === 'removed') return 'removed'
  if (
    activity.viewerStatus === 'joined' ||
    activity.viewerStatus === 'attended'
  ) {
    return 'joined'
  }
  if (activity.viewerStatus === 'pending') return 'pending'
  if (activity.viewerStatus === 'waitlisted') return 'waitlisted'
  if (activity.status === 'cancelled') return 'cancelled'
  if (activity.joinWindowClosed) return 'closed'
  if (activity.canWaitlist) return 'waitlist'
  if (activity.canJoin) {
    if (activity.participationMode === 'approval') return 'join-approval'
    if (activity.participationMode === 'invite') return 'join-invite'
    return 'join-open'
  }
  if (activity.availableSpots <= 0) return 'full'
  return 'unavailable'
}

function participationCopy(
  kind: ParticipationKind,
  activity: ActivityDetail,
): ParticipationCopy {
  switch (kind) {
    case 'creator':
      return {
        titleKey: 'activityDetail.participation.creatorTitle',
        bodyKey: 'activityDetail.participation.creatorBody',
      }
    case 'joined':
      return {
        titleKey: 'activityDetail.participation.joinedTitle',
        bodyKey: 'activityDetail.participation.joinedBody',
      }
    case 'pending':
      return {
        titleKey: 'activityDetail.participation.pendingTitle',
        bodyKey: 'activityDetail.participation.pendingBody',
      }
    case 'waitlisted':
      return {
        titleKey: 'activityDetail.participation.waitlistedTitle',
        bodyKey:
          activity.waitlistPosition == null
            ? 'activityDetail.participation.waitlistedBodyNoPosition'
            : 'activityDetail.participation.waitlistedBody',
        bodyValues:
          activity.waitlistPosition == null
            ? undefined
            : { position: activity.waitlistPosition },
      }
    case 'removed':
      return {
        titleKey: 'activityDetail.participation.removedTitle',
        bodyKey: 'activityDetail.participation.removedBody',
      }
    case 'waitlist':
      return {
        titleKey: 'activityDetail.participation.waitlistTitle',
        bodyKey: 'activityDetail.participation.waitlistBody',
      }
    case 'join-open':
      return {
        titleKey: 'activityDetail.participation.openTitle',
        bodyKey: 'activityDetail.participation.openBody',
      }
    case 'join-approval':
      return {
        titleKey: 'activityDetail.participation.approvalTitle',
        bodyKey: 'activityDetail.participation.approvalBody',
      }
    case 'join-profile':
      return {
        titleKey: 'activityDetail.participation.profileBlockedTitle',
        bodyKey: 'activityDetail.participation.profileBlockedBody',
      }
    case 'join-invite': {
      const location =
        activity.locationName?.trim() || activity.city?.trim() || ''
      return {
        titleKey: 'activityDetail.participation.inviteTitle',
        bodyKey: location
          ? 'activityDetail.participation.inviteBody'
          : 'activityDetail.participation.inviteBodyNoLocation',
        bodyValues: { count: activity.availableSpots, location },
      }
    }
    case 'closed':
      return {
        titleKey: 'activityDetail.participation.closedTitle',
        bodyKey: 'activityDetail.participation.closed',
      }
    case 'full':
      return {
        titleKey: 'activityDetail.participation.fullTitle',
        bodyKey: 'activityDetail.participation.full',
      }
    case 'cancelled':
      return {
        titleKey: 'activityDetail.status.cancelledTitle',
        bodyKey: 'activityDetail.status.cancelledBody',
      }
    case 'unavailable':
      return {
        titleKey: 'activityDetail.participation.unavailableTitle',
        bodyKey: 'activityDetail.participation.unavailable',
      }
  }
}

/**
 * Participation sidebar. Join/leave call the frozen endpoints after an explicit
 * confirmation; full activities expose the waitlist instead of a hard error
 * (API_CONTRACT §6.10/§6.11). Save/report/block stay in this panel.
 */
export function ActionPanel({
  activity,
  profileIncomplete,
  saved,
  saveBusy,
  joinBusy,
  leaveBusy,
  canBlock,
  canReport,
  onToggleSave,
  onReport,
  onBlock,
  onJoin,
  onLeave,
}: ActionPanelProps) {
  const { t } = useTranslation()
  const reasonId = useId()
  const descriptionId = useId()
  const blockBodyId = useId()

  const [reportOpen, setReportOpen] = useState(false)
  const [blockOpen, setBlockOpen] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null)
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [description, setDescription] = useState('')
  const [actionBusy, setActionBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [reasonError, setReasonError] = useState(false)

  const kind = participationKind(activity, profileIncomplete)
  const copy = participationCopy(kind, activity)
  const canLeave = canLeaveActivity(activity)
  const showMoreActions = canBlock
  const isJoinAction =
    kind === 'waitlist' ||
    kind === 'join-open' ||
    kind === 'join-approval' ||
    kind === 'join-invite' ||
    kind === 'join-profile'
  const isLeaveAction = kind === 'pending' || kind === 'waitlisted'

  const joinCtaKey =
    kind === 'waitlist'
      ? 'activityDetail.participation.ctaWaitlist'
      : kind === 'join-approval'
        ? 'activityDetail.participation.ctaApproval'
        : kind === 'join-invite'
          ? 'activityDetail.participation.ctaInvite'
          : 'activityDetail.participation.ctaOpen'

  const joinDialog =
    kind === 'waitlist'
      ? {
          titleKey: 'activityDetail.join.waitlistTitle',
          bodyKey: 'activityDetail.join.waitlistBody',
          confirmKey: 'activityDetail.join.confirmWaitlist',
        }
      : kind === 'join-approval'
        ? {
            titleKey: 'activityDetail.join.approvalTitle',
            bodyKey: 'activityDetail.join.approvalBody',
            confirmKey: 'activityDetail.join.confirmApproval',
          }
        : kind === 'join-invite'
          ? {
              titleKey: 'activityDetail.join.inviteTitle',
              bodyKey: 'activityDetail.join.inviteBody',
              confirmKey: 'activityDetail.join.confirmInvite',
            }
          : {
              titleKey: 'activityDetail.join.openTitle',
              bodyKey: 'activityDetail.join.openBody',
              confirmKey: 'activityDetail.join.confirmOpen',
            }

  const leaveDialog =
    kind === 'waitlisted'
      ? {
          titleKey: 'activityDetail.leave.waitlistTitle',
          bodyKey: 'activityDetail.leave.waitlistBody',
          confirmKey: 'activityDetail.leave.confirmWaitlist',
        }
      : kind === 'pending'
        ? {
            titleKey: 'activityDetail.leave.pendingTitle',
            bodyKey: 'activityDetail.leave.pendingBody',
            confirmKey: 'activityDetail.leave.confirmPending',
          }
        : {
            titleKey: 'activityDetail.leave.joinedTitle',
            bodyKey: 'activityDetail.leave.joinedBody',
            confirmKey: 'activityDetail.leave.confirmJoined',
          }

  async function submitReport() {
    if (!reason) {
      setReasonError(true)
      return
    }
    setActionBusy(true)
    setActionError(null)
    setReasonError(false)
    try {
      await onReport(reason, description.trim())
      setReportOpen(false)
      setReason('')
      setDescription('')
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    } finally {
      setActionBusy(false)
    }
  }

  async function confirmBlock() {
    setActionBusy(true)
    setActionError(null)
    try {
      await onBlock()
      setBlockOpen(false)
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    } finally {
      setActionBusy(false)
    }
  }

  async function confirmParticipation() {
    if (!confirm) return
    setActionError(null)
    try {
      const ok = confirm === 'join' ? await onJoin() : await onLeave()
      if (ok) setConfirm(null)
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    }
  }

  const activeDialog =
    confirm === 'join' ? joinDialog : confirm === 'leave' ? leaveDialog : null

  return (
    <aside
      aria-label={t('activityDetail.actions.title')}
      className={`rounded-card p-4 min-[960px]:sticky min-[960px]:top-6 ${
        activity.participationMode === 'approval' ? 'bg-mint' : 'bg-[#fff1ee]'
      }`}
    >
      <p className="font-body text-[18px] font-bold text-deep">
        {t(copy.titleKey)}
      </p>
      <p className="mt-1 text-[15px] leading-relaxed text-muted">
        {t(copy.bodyKey, copy.bodyValues)}
      </p>

      {isJoinAction ? (
        <p className="mt-2 text-[15px] font-semibold text-deep">
          {t('activityDetail.availableSpots', {
            count: activity.availableSpots,
          })}
          {activity.locationName ? ` · ${activity.locationName}` : null}
        </p>
      ) : null}

      {isJoinAction ? (
        <div className="mt-4 space-y-2">
          <button
            type="button"
            className="btn-primary w-full"
            disabled={joinBusy || kind === 'join-profile'}
            onClick={() => {
              setActionError(null)
              setConfirm('join')
            }}
          >
            {joinBusy
              ? t('activityDetail.participation.joining')
              : t(joinCtaKey)}
          </button>
        </div>
      ) : null}

      {isLeaveAction ? (
        <div className="mt-4">
          <button
            type="button"
            className="btn-secondary w-full bg-white"
            disabled={leaveBusy || !canLeave}
            onClick={() => {
              setActionError(null)
              setConfirm('leave')
            }}
          >
            {kind === 'waitlisted'
              ? t('activityDetail.participation.ctaLeaveWaitlist')
              : t('activityDetail.participation.ctaCancel')}
          </button>
          {!canLeave ? (
            <p className="mt-2 text-xs leading-relaxed text-muted">
              {t('activityDetail.participation.leaveClosed')}
            </p>
          ) : null}
        </div>
      ) : null}

      {kind === 'joined' ? (
        <div className="mt-4 space-y-2">
          <Link
            to={`/chat/${encodeURIComponent(activity.id)}`}
            className="btn-primary w-full"
          >
            {t('activityDetail.participation.ctaChat')}
          </Link>
          <button
            type="button"
            className="btn-secondary w-full bg-white"
            disabled={leaveBusy || !canLeave}
            onClick={() => {
              setActionError(null)
              setConfirm('leave')
            }}
          >
            {leaveBusy
              ? t('activityDetail.participation.leaving')
              : t('activityDetail.participation.ctaLeave')}
          </button>
          {!canLeave ? (
            <p className="text-xs leading-relaxed text-muted">
              {t('activityDetail.participation.leaveClosed')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        className={`mt-4 grid gap-2 border-t border-line pt-4 ${
          canReport ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        <button
          type="button"
          className="btn-secondary w-full bg-white"
          aria-pressed={saved}
          disabled={saveBusy}
          onClick={onToggleSave}
        >
          <Heart
            size={20}
            weight={saved ? 'fill' : kuniriIconWeight}
            aria-hidden="true"
          />
          {saveBusy
            ? t(
                saved
                  ? 'activityDetail.actions.removing'
                  : 'activityDetail.actions.saving',
              )
            : t(
                saved
                  ? 'activityDetail.actions.saved'
                  : 'activityDetail.actions.save',
              )}
        </button>

        {canReport ? (
          <button
            type="button"
            className="btn-secondary w-full bg-white"
            aria-expanded={reportOpen}
            onClick={() => {
              setActionError(null)
              setReportOpen((open) => !open)
              setBlockOpen(false)
            }}
          >
            <KuniriIcons.flag
              size={19}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.actions.report')}
          </button>
        ) : null}
      </div>

      {showMoreActions ? (
        <details className="mt-4 rounded-md border border-line bg-paper p-3">
          <summary className="min-h-11 cursor-pointer py-2 text-sm font-bold text-deep">
            {t('activityDetail.actions.more')}
          </summary>
          <div className="mt-2 flex flex-col gap-2">
            {canBlock ? (
              <button
                type="button"
                className="btn-ghost min-w-0 justify-start px-2 text-left text-sm whitespace-normal text-danger hover:bg-[var(--danger-soft)]"
                aria-expanded={blockOpen}
                onClick={() => {
                  setActionError(null)
                  setBlockOpen((open) => !open)
                  setReportOpen(false)
                }}
              >
                <KuniriIcons.userMinus
                  size={18}
                  weight={kuniriIconWeight}
                  aria-hidden="true"
                />
                {t('activityDetail.actions.block', {
                  name: activity.creatorName,
                })}
              </button>
            ) : null}
          </div>
        </details>
      ) : null}

      {reportOpen ? (
        <form
          className="mt-3 space-y-3 rounded-md border border-line bg-paper p-3"
          onSubmit={(event) => {
            event.preventDefault()
            void submitReport()
          }}
        >
          <h3 className="font-head text-base font-extrabold text-deep">
            {t('activityDetail.actions.reportTitle')}
          </h3>
          <div>
            <label
              htmlFor={reasonId}
              className="field-label text-sm"
            >
              {t('activityDetail.actions.reportReason')}
            </label>
            <select
              id={reasonId}
              className="field-input"
              value={reason}
              aria-invalid={reasonError || undefined}
              aria-describedby={reasonError ? `${reasonId}-error` : undefined}
              onChange={(event) => {
                setReason(event.target.value as ReportReason | '')
                setReasonError(false)
              }}
            >
              <option value="">
                {t('activityDetail.actions.reportReasonPlaceholder')}
              </option>
              {REPORT_REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`activityDetail.actions.reasons.${value}`)}
                </option>
              ))}
            </select>
            {reasonError ? (
              <p
                id={`${reasonId}-error`}
                role="alert"
                className="mt-1 text-xs font-semibold text-danger"
              >
                {t('activityDetail.actions.reportReasonRequired')}
              </p>
            ) : null}
          </div>
          <div>
            <label
              htmlFor={descriptionId}
              className="field-label text-sm"
            >
              {t('activityDetail.actions.reportDescription')}
            </label>
            <textarea
              id={descriptionId}
              className="field-input min-h-24"
              maxLength={2000}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className="btn-primary min-h-11 px-4 text-sm"
              disabled={actionBusy}
            >
              {actionBusy
                ? t('activityDetail.actions.reportSubmitting')
                : t('activityDetail.actions.reportSubmit')}
            </button>
            <button
              type="button"
              className="btn-ghost px-2 text-sm"
              disabled={actionBusy}
              onClick={() => {
                setReportOpen(false)
                setReasonError(false)
              }}
            >
              {t('activityDetail.actions.reportCancel')}
            </button>
          </div>
        </form>
      ) : null}

      {blockOpen ? (
        <div className="mt-3 space-y-3 rounded-md border border-danger/30 bg-[var(--danger-soft)] p-3">
          <h3 className="font-head text-base font-extrabold text-deep">
            {t('activityDetail.actions.blockTitle', {
              name: activity.creatorName,
            })}
          </h3>
          <p id={blockBodyId} className="text-sm leading-relaxed text-deep">
            {t('activityDetail.actions.blockBody')}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary min-h-11 bg-danger px-4 text-sm text-white"
              disabled={actionBusy}
              aria-describedby={blockBodyId}
              onClick={() => void confirmBlock()}
            >
              {t('activityDetail.actions.blockConfirm')}
            </button>
            <button
              type="button"
              className="btn-ghost px-2 text-sm"
              disabled={actionBusy}
              onClick={() => setBlockOpen(false)}
            >
              {t('activityDetail.actions.blockCancel')}
            </button>
          </div>
        </div>
      ) : null}

      {actionError && confirm === null ? (
        <p role="alert" className="mt-3 text-sm font-semibold text-danger">
          {t(actionError)}
        </p>
      ) : null}

      <ConfirmDialog
        open={activeDialog !== null}
        titleKey={activeDialog?.titleKey ?? 'activityDetail.join.openTitle'}
        titleValues={{ title: activity.title }}
        bodyKey={activeDialog?.bodyKey ?? 'activityDetail.join.openBody'}
        bodyValues={{ title: activity.title }}
        confirmKey={activeDialog?.confirmKey ?? 'activityDetail.join.confirmOpen'}
        cancelKey={
          confirm === 'leave'
            ? 'activityDetail.leave.cancel'
            : 'activityDetail.join.cancel'
        }
        tone="default"
        busy={confirm === 'join' ? joinBusy : leaveBusy}
        onConfirm={() => void confirmParticipation()}
        onCancel={() => {
          const busy = confirm === 'join' ? joinBusy : leaveBusy
          if (!busy) setConfirm(null)
        }}
      >
        {actionError ? (
          <p role="alert" className="text-sm font-semibold text-danger">
            {t(actionError)}
          </p>
        ) : null}
      </ConfirmDialog>
    </aside>
  )
}
